import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "../Icon";
import { COMPANY } from "../data";
import { useAuth } from "../auth";
import { useCart } from "../cart";
import { useSeo } from "../ui";
import { CartThumb, FwLine, LoginRequired, shippingFor } from "./Cart";
import { cancelUnpaid, completePayment, openCardPayment, payConfig, savePending } from "../pay";

export function BankBox({ amount }) {
  const { bank } = COMPANY;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(bank.account.replace(/-/g, ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* 클립보드를 쓸 수 없으면 번호를 직접 보고 적으면 된다 */
    }
  };

  return (
    <div className="bank">
      <div className="bank__row">
        <span className="bank__label">입금 은행</span>
        <strong>{bank.name}</strong>
      </div>
      <div className="bank__row">
        <span className="bank__label">계좌번호</span>
        <strong className="bank__account">{bank.account}</strong>
        <button type="button" className="bank__copy" onClick={copy}>{copied ? "복사됨" : "복사"}</button>
      </div>
      <div className="bank__row">
        <span className="bank__label">예금주</span>
        <strong>{bank.holder}</strong>
      </div>
      {amount != null && (
        <div className="bank__row bank__row--amount">
          <span className="bank__label">입금할 금액</span>
          <strong>{amount.toLocaleString("ko-KR")}원</strong>
        </div>
      )}
    </div>
  );
}

/* 카드결제 완료 화면 — 주문서(PC)와 /checkout/complete(모바일 결제창에서 돌아옴)가 같이 쓴다 */
export function CardDone({ order, email }) {
  const pay = order.pay || {};
  if (order.test_only) {
    /* 카드결제 심사 기간(PORTONE_MODE=review) — 테스트 채널 결제라 청구되지 않았고 주문도 확정하지 않는다 */
    return (
      <div className="order-done">
        <h1 className="shop__title">테스트 결제가 완료되었습니다</h1>
        <p className="order-done__no">주문번호 #{order.order_no}</p>
        <p className="order-done__desc">
          지금 카드결제는 결제대행사 심사를 위한 테스트 상태입니다.<br />
          실제로 청구되지 않았고, 주문도 확정되지 않았습니다.<br />
          실제 주문은 결제 방법에서 <strong>계좌이체</strong>를 골라 주세요.
        </p>
        <div className="order-done__actions">
          <Link to="/cart" className="btn btn--ghost">장바구니로</Link>
          <Link to="/my?tab=orders" className="btn btn--primary">주문내역 보기</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="order-done">
      <div className="order-done__icon"><Icon.Check width="28" height="28" aria-hidden="true" focusable="false" /></div>
      <h1 className="shop__title">결제가 완료되었습니다</h1>
      <p className="order-done__no">주문번호 #{order.order_no}</p>
      <p className="order-done__desc">
        준비되는 대로 발송해 드립니다.{email && <><br />주문 내역은 {email} 로도 보내드렸습니다.</>}
      </p>
      <div className="bank">
        <div className="bank__row">
          <span className="bank__label">결제 수단</span>
          <strong>{pay.card || "카드"}{pay.installment ? ` · ${pay.installment}개월 할부` : ""}{pay.test ? " (테스트 결제)" : ""}</strong>
        </div>
        <div className="bank__row bank__row--amount">
          <span className="bank__label">결제 금액</span>
          <strong>{Number(order.total || 0).toLocaleString("ko-KR")}원</strong>
        </div>
      </div>
      {pay.receipt_url && (
        <p className="order-done__desc order-done__desc--dim">
          <a href={pay.receipt_url} target="_blank" rel="noreferrer">카드 영수증 보기</a>
        </p>
      )}
      <div className="order-done__actions">
        <Link to="/products" className="btn btn--ghost">쇼핑 계속하기</Link>
        <Link to="/my?tab=orders" className="btn btn--primary">주문내역 보기</Link>
      </div>
    </div>
  );
}

/* 주소 검색 — 카카오(다음) 우편번호 서비스. 키 없이 쓰는 무료 서비스라 스크립트만 불러온다.
   팝업은 모바일에서 막히기 쉬워 화면 안 레이어(embed)로 띄운다. */
const POSTCODE_SRC = "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
let postcodeLoading = null;

function loadPostcode() {
  if (window.daum && window.daum.Postcode) return Promise.resolve();
  if (!postcodeLoading) {
    postcodeLoading = new Promise((resolve, reject) => {
      const el = document.createElement("script");
      el.src = POSTCODE_SRC;
      el.async = true;
      el.onload = () => resolve();
      el.onerror = () => { postcodeLoading = null; reject(new Error("load failed")); };
      document.head.appendChild(el);
    });
  }
  return postcodeLoading;
}

function AddressSearch({ onSelect, onClose }) {
  const boxRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    loadPostcode()
      .then(() => {
        if (!alive || !boxRef.current) return;
        new window.daum.Postcode({
          width: "100%",
          height: "100%",
          oncomplete(data) {
            let address = data.userSelectedType === "R" ? data.roadAddress : data.jibunAddress;
            /* 도로명 주소면 법정동·건물명을 괄호로 붙인다(우편번호 서비스 권장 방식) */
            if (data.userSelectedType === "R") {
              const extra = [];
              if (data.bname && /[동로가]$/.test(data.bname)) extra.push(data.bname);
              if (data.buildingName && data.apartment === "Y") extra.push(data.buildingName);
              if (extra.length) address += ` (${extra.join(", ")})`;
            }
            onSelect({ zip: data.zonecode, address });
          },
        }).embed(boxRef.current, { autoClose: false });
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [onSelect]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="addr-search" role="presentation" onClick={onClose}>
      <div className="addr-search__box" role="dialog" aria-modal="true" aria-label="주소 검색" onClick={(e) => e.stopPropagation()}>
        <div className="addr-search__head">
          <strong>주소 검색</strong>
          <button type="button" className="addr-search__close" onClick={onClose} aria-label="닫기">×</button>
        </div>
        {failed ? (
          <p className="addr-search__fail">
            주소 검색을 불러오지 못했습니다. 네트워크를 확인하시거나 주소를 직접 입력해 주세요.
          </p>
        ) : (
          <div ref={boxRef} className="addr-search__frame" />
        )}
      </div>
    </div>
  );
}

/* 주문서 — 결제는 계좌이체, 그리고 포트원 키가 들어가 있으면 카드결제(pay.js).
   바로구매로 들어오면 그 상품만, 아니면 장바구니 전체를 주문한다. */
export default function Checkout() {
  useSeo({ title: "주문서 | I.META", description: "주문 정보 입력", path: "/checkout", noindex: true });
  const { state } = useLocation();
  const cart = useCart();
  const { member, ready } = useAuth();
  const direct = state && Array.isArray(state.items) && state.items.length > 0;
  const items = direct ? state.items : cart.items;
  const subtotal = items.reduce((n, i) => n + (i.price || 0) * i.qty, 0);
  const shipping = shippingFor(subtotal);
  /* 펌웨어 개발·빌드(라오나봇 개발의뢰) — 장바구니 주문에만. 배송비는 상품 금액으로만 계산한다 */
  const fw = direct ? [] : cart.fw;
  const fwTotal = fw.reduce((n, r) => n + (r.fee || 0), 0);

  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [card, setCard] = useState(false); // 카드결제를 받을 수 있는지 (서버 설정)
  const [review, setReview] = useState(false); // 카드결제 심사 기간 — 테스트 결제만 된다
  const [method, setMethod] = useState("CARD");
  useEffect(() => {
    let alive = true;
    payConfig().then((c) => {
      if (!alive) return;
      setCard(!!c.card);
      setReview(c.mode === "review");
      if (c.mode === "review") setMethod("BANK"); // 심사 기간엔 실제 주문이 계좌이체로 오도록 기본값을 계좌이체로
    });
    return () => { alive = false; };
  }, []);
  const address2Ref = useRef(null);
  /* 우편번호 레이어가 렌더마다 다시 그려지지 않도록 콜백을 고정하고, 실제 처리는 최신 함수로 넘긴다 */
  const onAddressLatest = useRef(null);
  const onAddressRef = useCallback((v) => onAddressLatest.current && onAddressLatest.current(v), []);
  const closeSearch = useCallback(() => setSearching(false), []);

  if (!ready) return <section className="section shop"><p className="note-line">불러오는 중…</p></section>;
  if (!member) return <LoginRequired title="주문서" />;

  /* 로그인 정보가 준비된 뒤에 기본값을 채운다 */
  const f = form || {
    name: member.name || "",
    phone: member.phone || "",
    email: member.email || "",
    zip: "",
    address: "",
    address2: "",
    memo: "",
    depositor: member.name || "",
  };
  const upd = (k) => (e) => setForm({ ...f, [k]: e.target.value });

  const onAddress = ({ zip, address }) => {
    setForm({ ...f, zip, address, address2: "" });
    setSearching(false);
    /* 레이어가 닫힌 뒤 상세 주소 칸으로 */
    setTimeout(() => address2Ref.current && address2Ref.current.focus(), 50);
  };
  onAddressLatest.current = onAddress;

  const payBy = card ? method : "BANK";

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const res = await fetch("/api/orders", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...f,
          pay_method: payBy,
          depositor: payBy === "CARD" ? "" : f.depositor,
          items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
          laona_manual_ids: direct ? [] : cart.projects, // [주문하기]로 담은 라오나봇 프로젝트 — 자료 다운로드가 열린다
          laona_fw_ids: fw.map((r) => r.id), // [펌웨어 빌드 포함] — 서버가 빌드비 줄을 더한다
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          res.status === 401
            ? "로그인이 만료되었습니다. 다시 로그인해 주세요."
            : typeof data.detail === "string" ? data.detail : "주문을 접수하지 못했습니다. 잠시 후 다시 시도해 주세요."
        );
      }
      if (data.payment) {
        /* 카드결제 — 결제창을 띄운다. 모바일은 여기서 페이지를 떠나 /checkout/complete 로 돌아온다 */
        savePending({ order_no: data.order_no, fromCart: !direct, email: f.email });
        const res = await openCardPayment(data.payment);
        if (!res) return; // 리디렉션으로 떠나는 중
        if (res.code !== undefined) {
          await cancelUnpaid(data.order_no);
          throw new Error(res.message || "결제가 완료되지 않았습니다.");
        }
        const order = await completePayment(res.paymentId || data.payment.paymentId);
        setResult({ ...order, card: true });
        setStatus("done");
        if (!direct && !order.test_only) cart.clear(); // 테스트 결제는 주문이 확정되지 않았으니 장바구니를 남긴다
        window.scrollTo(0, 0);
        return;
      }
      setResult({ ...data, items });
      setStatus("done");
      if (!direct) cart.clear();
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
      setStatus("idle");
    }
  };

  if (status === "done" && result && result.card) {
    return <section className="section shop"><CardDone order={result} email={f.email} /></section>;
  }

  if (status === "done" && result) {
    return (
      <section className="section shop">
        <div className="order-done">
          <div className="order-done__icon"><Icon.Check width="28" height="28" aria-hidden="true" focusable="false" /></div>
          <h1 className="shop__title">주문이 접수되었습니다</h1>
          <p className="order-done__no">주문번호 #{result.order_no}</p>
          <p className="order-done__desc">
            아래 계좌로 입금해 주시면 확인 후 바로 발송해 드립니다.<br />
            입금 안내는 {f.email} 로도 보내드렸습니다.
          </p>
          <BankBox amount={result.total} />
          <p className="order-done__desc order-done__desc--dim">
            입금자명 <strong>{f.depositor}</strong> 으로 입금해 주세요. 이름이 다르면 확인이 늦어질 수 있습니다.
          </p>
          <div className="order-done__actions">
            <Link to="/products" className="btn btn--ghost">쇼핑 계속하기</Link>
            <Link to="/my?tab=orders" className="btn btn--primary">주문내역 보기</Link>
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0 && fw.length === 0) {
    return (
      <section className="section shop">
        <div className="shop-empty">
          <Icon.Cart width="30" height="30" aria-hidden="true" focusable="false" />
          <p>주문할 상품이 없습니다.</p>
          <Link to="/products" className="btn btn--primary">판매제품 보러가기</Link>
        </div>
      </section>
    );
  }

  const busy = status === "sending";

  return (
    <section className="section shop">
      <div className="shop__head">
        <h1 className="shop__title">주문서</h1>
        {!direct && <Link to="/cart" className="shop__cartlink">← 장바구니</Link>}
      </div>

      <form className="cart" onSubmit={submit}>
        <div className="cart__list">
          <h2 className="checkout__h">주문 상품</h2>
          {items.map((i) => (
            <div key={i.slug} className="cart-item cart-item--static">
              <CartThumb src={i.thumb} name={i.name} />
              <div className="cart-item__info">
                <div className="cart-item__name">{i.name}</div>
                <div className="cart-item__unit">
                  {(i.price || 0).toLocaleString("ko-KR")}원 × {i.qty}개
                </div>
              </div>
              <div className="cart-item__sum">{((i.price || 0) * i.qty).toLocaleString("ko-KR")}원</div>
            </div>
          ))}
          {fw.map((r) => <FwLine key={`fw${r.id}`} row={r} />)}
          {fw.length > 0 && (
            <p className="cart__hint" style={{ textAlign: "left" }}>
              펌웨어 개발·빌드는 기본 5만원이며, 작업량에 따라 추가 비용이 최대 10만원까지 발생할 수 있습니다. 다만 일반적으로 추가 비용은 발생하지 않습니다.
              입금이 확인되면 개발을 시작합니다.
            </p>
          )}

          <h2 className="checkout__h">주문자 · 배송 정보</h2>
          <div className="checkout__grid">
            <label className="form__field">
              <span className="form__label">받는 분<span className="form__req">*</span></span>
              <input className="form__input" required value={f.name} onChange={upd("name")} autoComplete="name" disabled={busy} />
            </label>
            <label className="form__field">
              <span className="form__label">연락처<span className="form__req">*</span></span>
              <input className="form__input" required value={f.phone} onChange={upd("phone")} autoComplete="tel" placeholder="010-0000-0000" disabled={busy} />
            </label>
            <label className="form__field checkout__full">
              <span className="form__label">이메일<span className="form__req">*</span></span>
              <input className="form__input" type="email" required value={f.email} onChange={upd("email")} autoComplete="email" disabled={busy} />
            </label>
            <div className="form__field checkout__full">
              <label className="form__label" htmlFor="co-zip">우편번호</label>
              <div className="addr-row">
                <input
                  id="co-zip" className="form__input addr-row__zip" value={f.zip} onChange={upd("zip")}
                  autoComplete="postal-code" inputMode="numeric" disabled={busy}
                />
                <button type="button" className="btn btn--ghost addr-row__btn" onClick={() => setSearching(true)} disabled={busy}>
                  <Icon.Search width="15" height="15" aria-hidden="true" focusable="false" /> 주소 검색
                </button>
              </div>
            </div>
            <label className="form__field checkout__full">
              <span className="form__label">주소<span className="form__req">*</span></span>
              <input
                className="form__input" required value={f.address} onChange={upd("address")}
                onClick={() => { if (!f.address) setSearching(true); }}
                autoComplete="address-line1" placeholder="주소 검색을 눌러 찾아 주세요" disabled={busy}
              />
            </label>
            <label className="form__field checkout__full">
              <span className="form__label">상세 주소</span>
              <input
                ref={address2Ref} className="form__input" value={f.address2} onChange={upd("address2")}
                autoComplete="address-line2" placeholder="동 · 호수 등" disabled={busy}
              />
            </label>
            <label className="form__field checkout__full">
              <span className="form__label">배송 메모</span>
              <input className="form__input" value={f.memo} onChange={upd("memo")} placeholder="부재 시 문 앞에 놓아주세요" disabled={busy} />
            </label>
          </div>

          {card ? (
            <>
              <h2 className="checkout__h">결제 방법</h2>
              <div className="checkout__pay" role="radiogroup" aria-label="결제 방법">
                {[["CARD", "신용 · 체크카드"], ["BANK", "계좌이체"]].map(([v, l]) => (
                  <label key={v} className={`chip${method === v ? " is-active" : ""}`}>
                    <input type="radio" name="pay_method" value={v} checked={method === v} onChange={() => setMethod(v)} disabled={busy} />
                    {l}
                  </label>
                ))}
              </div>
            </>
          ) : (
            <h2 className="checkout__h">결제 방법 — 계좌이체</h2>
          )}
          {card && review && (
            <p className="checkout__review" role="note">
              카드결제는 현재 결제대행사 심사를 위한 <strong>테스트 상태</strong>입니다. 카드로 결제해도 실제로 청구되지 않으며
              주문이 확정되지 않습니다. 실제 주문은 <strong>계좌이체</strong>로 해 주세요.
            </p>
          )}
          {payBy === "CARD" ? (
            <p className="cart__hint" style={{ textAlign: "left", marginTop: 12 }}>
              [카드로 결제하기]를 누르면 카드 결제창이 열립니다. 결제가 끝나면 바로 주문이 확정되고 발송을 준비합니다.
              세금계산서가 필요하시면 배송 메모에 적어 주세요.
            </p>
          ) : (
            <>
              {card && <div style={{ height: 12 }} />}
              <BankBox />
              <label className="form__field" style={{ marginTop: 14, maxWidth: 320 }}>
                <span className="form__label">입금자명<span className="form__req">*</span></span>
                <input className="form__input" required value={f.depositor} onChange={upd("depositor")} disabled={busy} />
              </label>
              <p className="cart__hint" style={{ textAlign: "left", marginTop: 8 }}>
                주문 후 위 계좌로 입금해 주시면 확인 후 발송합니다. 세금계산서가 필요하시면 배송 메모에 적어 주세요.
              </p>
            </>
          )}
        </div>

        <aside className="cart__summary">
          <div className="cart__row"><span>상품금액</span><span>{subtotal.toLocaleString("ko-KR")}원</span></div>
          {fwTotal > 0 && (
            <div className="cart__row"><span>펌웨어 개발·빌드</span><span>{fwTotal.toLocaleString("ko-KR")}원</span></div>
          )}
          <div className="cart__row">
            <span>배송비</span>
            <span>{shipping === 0 ? "무료" : `${shipping.toLocaleString("ko-KR")}원`}</span>
          </div>
          <div className="cart__row cart__row--total">
            <span>{payBy === "CARD" ? "결제할 금액" : "입금할 금액"}</span>
            <strong>{(subtotal + shipping + fwTotal).toLocaleString("ko-KR")}원</strong>
          </div>
          <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
            {payBy === "CARD"
              ? (busy ? "결제 진행 중…" : "카드로 결제하기")
              : (busy ? "주문 접수 중…" : "주문하기 (계좌이체)")}
          </button>
          {error && <p className="cart__notice cart__notice--error" role="alert">{error}</p>}
          <p className="cart__notice checkout__refund">
            주문 전 <Link to="/refund" target="_blank" rel="noopener">교환 · 환불 안내</Link>를 확인해 주세요.
          </p>
        </aside>
      </form>

      {searching && <AddressSearch onSelect={onAddressRef} onClose={closeSearch} />}
    </section>
  );
}
